import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, Info, AlertTriangle } from 'lucide-react';
import * as agentsApi from '@/api/agents.api';
import * as routingApi from '@/api/notification-routing.api';
import type { InspectorConfig } from '@/api/installation.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { Modal } from '@/components/ui/Modal';
import { inspectorWarnings } from '@/lib/inspector-warnings';
import { cn } from '@/lib/cn';

/**
 * Pestaña del AGENTE INSPECTOR: el segundo agente que corre ANTES de la Asesora
 * y decide si ella entra.
 *
 * TODO acá es contenido del dueño. La interfaz nace COMPLETA pero VACÍA: sin
 * indicadores, sin tipos de caso, sin textos y con el switch maestro apagado —
 * mientras no se llene, el sistema se comporta como antes.
 *
 * Los destinatarios NO son una agenda nueva: se eligen del directorio de números
 * que ya vive en Mensajes → «¿A quién le llegan los avisos internos?».
 */

/** Id local para las filas nuevas (el server solo exige que sea único). */
const nuevoId = (prefijo: string) =>
  `${prefijo}${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;

export function InspectorTab({
  value,
  onChange,
}: {
  value: InspectorConfig;
  onChange: (patch: Partial<InspectorConfig>) => void;
}) {
  const modelos = useQuery({
    queryKey: ['agents', 'models'],
    queryFn: agentsApi.getModelCatalog,
  });
  const routing = useQuery({ queryKey: ['notification-routing'], queryFn: routingApi.getRouting });
  // Confirmación antes de borrar: el dueño no puede perder un texto de un clic.
  const [porBorrar, setPorBorrar] = useState<{
    tipo: 'indicador' | 'tipoCaso';
    id: string;
    nombre: string;
  } | null>(null);

  const numeros = routing.data?.config.numeros ?? [];
  const avisos = inspectorWarnings(value);

  function confirmarBorrado() {
    if (!porBorrar) return;
    if (porBorrar.tipo === 'indicador') {
      onChange({ indicadores: value.indicadores.filter((x) => x.id !== porBorrar.id) });
    } else {
      onChange({ tiposCaso: value.tiposCaso.filter((x) => x.id !== porBorrar.id) });
    }
    setPorBorrar(null);
  }

  return (
    <div className={cn('space-y-4', !value.activo && 'opacity-95')}>
      {/* Switch maestro. */}
      <Card>
        <CardBody className="flex flex-wrap items-start justify-between gap-3 p-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Agente Inspector
            </p>
            <p className="mt-0.5 max-w-2xl text-xs leading-snug text-slate-400">
              Revisa cada conversación nueva ANTES que la asesora y decide si ella contesta. Sirve
              para atender solo a los que llegan por publicidad y no meterse con quien ya venía
              hablando. Mientras esté apagado, todo funciona como hasta ahora.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant={value.activo ? 'success' : 'default'}>
              {value.activo ? 'Encendido' : 'Apagado'}
            </Badge>
            <Toggle
              checked={value.activo}
              onChange={(activo) => onChange({ activo })}
              label="Encender el Agente Inspector"
            />
          </div>
        </CardBody>
      </Card>

      {/* Avisos: lo que está a medio configurar y por qué no va a funcionar. */}
      {avisos.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700/60 dark:bg-amber-900/20">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 shrink-0" /> Revisa esto
          </p>
          <ul className="mt-1.5 list-disc space-y-1 pl-6 text-xs text-amber-900 dark:text-amber-200">
            {avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Capas de decisión. */}
      <Card>
        <CardHeader>
          <CardTitle>Cómo decide</CardTitle>
          <CardDescription>
            Se revisan en este orden y gana la primera que resuelva. Puedes apagar cualquiera.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          <CapaRow
            titulo="1. Vino de un anuncio (señal de WhatsApp)"
            detalle="WhatsApp nos avisa cuando el chat nació de un click en un anuncio de Facebook o Instagram. Es un dato del sistema, no una suposición: si está, la asesora entra."
            checked={value.capas.senalAnuncio}
            onChange={(senalAnuncio) => onChange({ capas: { ...value.capas, senalAnuncio } })}
          />
          <CapaRow
            titulo="2. Ya habíamos hablado antes"
            detalle="Si el contacto tiene conversaciones anteriores registradas, la asesora NO entra. Ojo: el sistema solo guarda unos días de historial, así que esto solo sirve para descartar."
            checked={value.capas.historial}
            onChange={(historial) => onChange({ capas: { ...value.capas, historial } })}
          />
          <CapaRow
            titulo="3. El mensaje trae uno de tus textos de publicidad"
            detalle="Los textos que cargues abajo. Si el primer mensaje contiene alguno, la asesora entra."
            checked={value.capas.indicadores}
            onChange={(indicadores) => onChange({ capas: { ...value.capas, indicadores } })}
          />
          <CapaRow
            titulo="4. El Inspector lee el mensaje y decide"
            detalle="Solo si las tres anteriores no alcanzaron. Consume un poco de IA por conversación nueva. Necesita que escribas sus instrucciones más abajo."
            checked={value.capas.intencion}
            onChange={(intencion) => onChange({ capas: { ...value.capas, intencion } })}
          />

          <div className="border-t border-surface-border pt-3 dark:border-slate-800">
            <Select
              label="Si ninguna alcanza para decidir…"
              value={value.porDefecto}
              onChange={(e) =>
                onChange({ porDefecto: e.target.value as InspectorConfig['porDefecto'] })
              }
              options={[
                { value: 'elegible', label: 'Que conteste la asesora (recomendado)' },
                { value: 'noElegible', label: 'Que quede para un humano' },
              ]}
            />
            <p className="mt-1 text-xs text-slate-400">
              Ante la duda conviene contestar: silenciar por defecto puede perder clientes reales.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* Instrucciones + modelo. */}
      <Card>
        <CardHeader>
          <CardTitle>Instrucciones del Inspector</CardTitle>
          <CardDescription>
            Solo se usan en el paso 4. El Inspector NUNCA le escribe al cliente: únicamente decide.
            Explicale qué consideras un primer contacto y qué suena a alguien que retoma algo (por
            ejemplo: «¿me confirmas lo de ayer?», «¿ya llegó el técnico?»).
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          <Textarea
            aria-label="Instrucciones del Inspector"
            rows={10}
            className="font-mono text-xs"
            placeholder="Escribe acá las instrucciones del Inspector. Mientras esté vacío, el paso 4 no se usa."
            value={value.prompt}
            onChange={(e) => onChange({ prompt: e.target.value })}
          />
          <Select
            label="Modelo del Inspector"
            value={value.modelo ?? ''}
            onChange={(e) => onChange({ modelo: e.target.value || null })}
            options={[
              { value: '', label: 'El mismo de la instalación' },
              ...(modelos.data?.modelos ?? []).map((m) => ({ value: m.id, label: m.nombre })),
            ]}
          />
          <p className="text-xs text-slate-400">
            Es una decisión corta: un modelo rápido y barato suele alcanzar.
          </p>
        </CardBody>
      </Card>

      {/* Indicadores de publicidad. */}
      <Card>
        <CardHeader>
          <CardTitle>Textos de tu publicidad</CardTitle>
          <CardDescription>
            Los mensajes que trae la gente que llega por un anuncio (el texto pre-cargado del botón,
            tu frase de campaña, etc.). Puedes cargar varios y apagar los que no estén corriendo,
            sin borrarlos.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-2">
          {value.indicadores.length === 0 && (
            <p className="flex items-start gap-1.5 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Todavía no cargaste ninguno. Sin textos, este paso no decide nada.
            </p>
          )}
          {value.indicadores.map((ind, i) => (
            <div key={ind.id} className="flex items-center gap-2">
              <Input
                aria-label={`Texto de publicidad ${i + 1}`}
                className="flex-1"
                placeholder="Ej.: Hola, vi su anuncio de artículos"
                value={ind.texto}
                onChange={(e) => {
                  const next = [...value.indicadores];
                  next[i] = { ...ind, texto: e.target.value };
                  onChange({ indicadores: next });
                }}
              />
              <Toggle
                checked={ind.activo}
                onChange={(activo) => {
                  const next = [...value.indicadores];
                  next[i] = { ...ind, activo };
                  onChange({ indicadores: next });
                }}
                label={`Texto ${i + 1}: encendido o apagado`}
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Quitar texto ${i + 1}`}
                className="h-8 w-8 text-slate-400 hover:text-red-600"
                onClick={() =>
                  setPorBorrar({
                    tipo: 'indicador',
                    id: ind.id,
                    nombre: ind.texto.trim() || `texto ${i + 1}`,
                  })
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onChange({
                indicadores: [
                  ...value.indicadores,
                  { id: nuevoId('ind'), texto: '', activo: true },
                ],
              })
            }
          >
            <Plus className="h-4 w-4" /> Agregar texto
          </Button>
        </CardBody>
      </Card>

      {/* Tipos de caso + destinatarios. */}
      <Card>
        <CardHeader>
          <CardTitle>Tipos de caso y a quién le llega cada uno</CardTitle>
          <CardDescription>
            Crea los tipos que manejas tú (por ejemplo soporte, cobranza, garantía) y marca a quién
            avisarle. Los números salen del directorio de Mensajes → «¿A quién le llegan los avisos
            internos?».
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {value.tiposCaso.length === 0 && (
            <p className="flex items-start gap-1.5 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Todavía no creaste ninguno. Sin tipos de caso, no se avisa a nadie.
            </p>
          )}
          {value.tiposCaso.map((tipo, i) => (
            <div
              key={tipo.id}
              className="space-y-2 rounded-md border border-surface-border p-3 dark:border-slate-700"
            >
              <div className="flex items-center gap-2">
                <Input
                  aria-label={`Nombre del tipo de caso ${i + 1}`}
                  className="flex-1"
                  placeholder="Ej.: Soporte"
                  value={tipo.nombre}
                  onChange={(e) => {
                    const next = [...value.tiposCaso];
                    next[i] = { ...tipo, nombre: e.target.value };
                    onChange({ tiposCaso: next });
                  }}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Quitar tipo de caso ${i + 1}`}
                  className="h-8 w-8 text-slate-400 hover:text-red-600"
                  onClick={() =>
                    setPorBorrar({
                      tipo: 'tipoCaso',
                      id: tipo.id,
                      nombre: tipo.nombre.trim() || `tipo de caso ${i + 1}`,
                    })
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <span className="mr-1 text-xs text-slate-400">Avisar a:</span>
                {numeros.length === 0 ? (
                  <span className="text-xs text-slate-400">
                    No hay números cargados todavía (se agregan en Mensajes).
                  </span>
                ) : (
                  numeros.map((n) => {
                    const marcado = tipo.numeroIds.includes(n.id);
                    return (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => {
                          const next = [...value.tiposCaso];
                          next[i] = {
                            ...tipo,
                            numeroIds: marcado
                              ? tipo.numeroIds.filter((id) => id !== n.id)
                              : [...tipo.numeroIds, n.id],
                          };
                          onChange({ tiposCaso: next });
                        }}
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium transition-colors',
                          marcado
                            ? 'bg-brand-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
                        )}
                      >
                        {n.nombre}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onChange({
                tiposCaso: [...value.tiposCaso, { id: nuevoId('tc'), nombre: '', numeroIds: [] }],
              })
            }
          >
            <Plus className="h-4 w-4" /> Agregar tipo de caso
          </Button>
        </CardBody>
      </Card>

      {/* Qué hacer cuando NO entra la asesora. */}
      <Card>
        <CardHeader>
          <CardTitle>Cuando la asesora NO debe contestar</CardTitle>
          <CardDescription>
            La conversación siempre queda en la bandeja para que la tome una persona. Sobre eso
            puedes sumar las dos acciones de abajo; se combinan.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Mandarle un mensaje de cortesía al cliente
                </p>
                <p className="mt-0.5 text-xs text-slate-400">
                  Sale EXACTAMENTE lo que escribas acá. Nunca menciones que no hay registro de la
                  conversación: el cliente no tiene por qué enterarse.
                </p>
              </div>
              <Toggle
                checked={value.noElegible.enviarCortesia}
                onChange={(enviarCortesia) =>
                  onChange({ noElegible: { ...value.noElegible, enviarCortesia } })
                }
                label="Mandar cortesía al cliente"
              />
            </div>
            <Textarea
              aria-label="Mensaje de cortesía"
              rows={2}
              placeholder="Ej.: Buenas, en un momento le atiende uno de nuestros asesores."
              value={value.cortesia.texto}
              onChange={(e) => onChange({ cortesia: { texto: e.target.value } })}
            />
          </div>

          <div className="space-y-2 border-t border-surface-border pt-4 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Avisarle a la persona del tipo de caso
                </p>
                <p className="mt-0.5 text-xs text-slate-400">
                  Va por WhatsApp a los números marcados arriba. Variables disponibles:{' '}
                  {'{{cliente}} {{telefono}} {{tipo}} {{mensaje}}'}.
                </p>
              </div>
              <Toggle
                checked={value.noElegible.notificarDestinatario}
                onChange={(notificarDestinatario) =>
                  onChange({ noElegible: { ...value.noElegible, notificarDestinatario } })
                }
                label="Avisar al destinatario"
              />
            </div>
            <Textarea
              aria-label="Texto del aviso interno"
              rows={2}
              placeholder="Ej.: {{tipo}}: escribió {{cliente}} ({{telefono}}). Dijo: {{mensaje}}"
              value={value.avisoDestinatario}
              onChange={(e) => onChange({ avisoDestinatario: e.target.value })}
            />
          </div>
        </CardBody>
      </Card>

      {/* Confirmación de borrado: un clic no puede hacerte perder un texto. */}
      <Modal
        open={Boolean(porBorrar)}
        onClose={() => setPorBorrar(null)}
        title="¿Borrar esto?"
        description={porBorrar ? `Vas a quitar «${porBorrar.nombre}».` : ''}
        footer={
          <>
            <Button variant="outline" onClick={() => setPorBorrar(null)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmarBorrado}>
              Sí, borrar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Se quita de la lista. Todavía puedes arrepentirte: solo se guarda cuando tocas «Guardar
          cambios» arriba.
        </p>
      </Modal>
    </div>
  );
}

/** Fila de una capa de decisión: título + explicación llana + su switch. */
function CapaRow({
  titulo,
  detalle,
  checked,
  onChange,
}: {
  titulo: string;
  detalle: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-3', !checked && 'opacity-60')}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{titulo}</p>
        <p className="mt-0.5 text-xs leading-snug text-slate-400">{detalle}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} label={titulo} />
    </div>
  );
}
